import { css } from '@/styled-system/css';

/* Autocomplete Input Styles */

export const commuteInputContainer = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginTop: 'xs', // 4px spacing from label
});

export const commuteInput = css({
  width: '100%',
  padding: '1x',
  paddingLeft: '32px', // 8px + 16px (icon) + 8px (gap)
  fontSize: 'md',
  color: 'hpxBlue600',
  bg: 'hpxWhite',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  borderRadius: '4px',
  outline: 'none',

  _placeholder: {
    color: 'hpxGrey500',
  },

  _focus: {
    borderColor: 'hpxTeal500',
    boxShadow: '0 0 0 2px',
    boxShadowColor: 'hpxTeal100',
  },

  _disabled: {
    bg: 'hpxGrey100',
    color: 'hpxGrey500',
    cursor: 'not-allowed',
  },
});

export const commuteIcon = css({
  position: 'absolute',
  left: '1x',
  zIndex: 'zIndexStep',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  color: 'hpxTeal500',
});

/* Autocomplete Dropdown Styles */

export const autocompleteDropdown = css({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 'zIndexParent',
  bg: 'hpxWhite',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  borderRadius: '4px',
  marginTop: '4px',
  maxHeight: '300px',
  overflowY: 'auto',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
});

export const autocompleteSuggestion = css({
  display: 'block',
  width: '100%',
  padding: '2x',
  textAlign: 'left',
  border: 'none',
  bg: 'transparent',
  cursor: 'pointer',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'hpxGrey100',

  _hover: {
    bg: 'hpxGrey100',
  },

  _focus: {
    bg: 'hpxTeal100',
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '-2px',
  },

  '&:last-child': {
    borderBottom: 'none',
  },
});

export const autocompleteSuggestionFocused = css({
  bg: 'hpxTeal100',
  outlineWidth: '2px',
  outlineStyle: 'solid',
  outlineColor: 'hpxTeal500',
  outlineOffset: '-2px',
});

export const suggestionPrimary = css({
  fontSize: 'md',
  color: 'hpxBlue600',
  fontWeight: '500',
  marginBottom: 'xs',
});

export const suggestionSecondary = css({
  fontSize: 'sm',
  color: 'hpxGrey600',
});

export const loadingSpinner = css({
  padding: '2x',
  textAlign: 'center',
  color: 'hpxGrey500',
  fontSize: 'sm',
  fontStyle: 'italic',
});

/* Commute Results Styles */

export const commuteResultsContainer = css({
  width: '100%',
});

export const commuteResultsHeader = css({
  fontSize: 'md',
  color: 'hpxBlue600',
  fontWeight: '500',
  marginBottom: '2x',

  _hover: {
    bg: 'hpxGrey100',
    borderRadius: '4px',
  },
});

export const commuteTimesLabel = css({
  fontSize: 'md', // 16px
  color: 'hpxBlue500',
  fontWeight: '700', // bold
});

export const commuteResultsGrid = css({
  display: 'flex',
  flexDirection: 'row',
  gap: '1x', // 8px gap between cards
  overflowX: 'auto', // Enable horizontal scroll
  overflowY: 'hidden',
  paddingBottom: '4px', // Space for focus ring
  scrollBehavior: 'smooth',

  // Hide scrollbar for cleaner appearance
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none', // Firefox
});

export const commuteResultsGridWithMargin = css({
  display: 'flex',
  flexDirection: 'row',
  gap: '1x', // 8px gap between cards
  overflowX: 'auto', // Enable horizontal scroll
  overflowY: 'hidden',
  paddingBottom: '4px', // Space for focus ring
  scrollBehavior: 'smooth',
  marginTop: 'xs', // 4px margin from label

  // Hide scrollbar for cleaner appearance
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none', // Firefox
});

export const commuteCard = css({
  display: 'flex',
  alignItems: 'center', // Center-align items
  gap: 'xs', // 4px between icon and content
  paddingTop: 'xs', // 4px vertical padding
  paddingBottom: 'xs', // 4px vertical padding
  paddingLeft: '1x', // 8px horizontal padding
  paddingRight: '1x', // 8px horizontal padding
  bg: 'hpxWhite',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  borderRadius: '8px',
  width: '116px', // Fixed width
  height: '56px', // Fixed height
  minWidth: '116px', // Prevent shrinking
  flexShrink: 0,

  // Interactive states
  cursor: 'pointer',
  _hover: {
    bg: 'hpxGrey100',
    borderColor: 'hpxTeal300',
  },
});

export const commuteCardIcon = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '24px',
  width: '24px',
  height: '24px',
  flexShrink: 0,
});

export const commuteCardContent = css({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
});

export const commuteCardTime = css({
  fontSize: 'sm', // 14px for readability
  fontWeight: '600',
  color: 'hpxBlue600',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const commuteCardActive = css({
  bg: 'hpxTeal100',
  borderColor: 'hpxTeal500',
  borderWidth: '2px',

  _hover: {
    bg: 'hpxTeal200',
    borderColor: 'hpxTeal600',
  },
});

export const commuteCardMode = css({
  fontSize: 'sm',
  color: 'hpxGrey600',
  marginTop: 'xs',
});

export const commuteCardDistance = css({
  fontSize: 'tiny',
  color: 'hpxGrey500',
  marginTop: 'xs',
});

export const loadingCard = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'xs', // 4px between icon and content
  paddingTop: 'xs', // 4px vertical padding
  paddingBottom: 'xs', // 4px vertical padding
  paddingLeft: '1x', // 8px horizontal padding
  paddingRight: '1x', // 8px horizontal padding
  bg: 'hpxGrey100',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  borderRadius: '8px',
  width: '116px', // Fixed width
  height: '56px', // Fixed height
  minWidth: '116px', // Prevent shrinking
  flexShrink: 0,
  opacity: 0.7,
});

export const errorCard = css({
  padding: '2x',
  bg: 'hpxRed100',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxRed400',
  borderRadius: '4px',
  color: 'hpxRed600',
  textAlign: 'center',
});

/* Screen Reader Only - Hidden visually but accessible to screen readers */
export const srOnly = css({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
